// Messages from the team (admin broadcasts), shown as a window over every
// screen: the menu, a table, even a blinded player's black screen. They come
// from GET /api/me/notices (polled every 30 s while the page is visible) and,
// at a table, straight over the lobby socket (net/notices.ts). Several queue
// up, oldest first. OK acknowledges one (POST /api/me/notices/ack) so it is
// not shown again here or on the player's other tabs and devices.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Notice } from "@wp/shared";
import { api } from "../../net/api";
import { onNotice } from "../../net/notices";
import { Modal } from "./Modal";

const POLL_MS = 30_000;
const DEFAULT_TITLE = "Message from the Wizard Poker team";

export function TeamNotices({ userId }: { userId: string | null }) {
  const [queue, setQueue] = useState<Notice[]>([]);
  /** id -> when this tab got it (a poll that started earlier cannot know it yet). */
  const receivedAt = useRef(new Map<number, number>());
  /** Acknowledged here (ack in flight or done): never queued again in this tab. */
  const acked = useRef(new Set<number>());

  const add = useCallback((incoming: Notice[], pollStartedAt: number | null) => {
    const now = Date.now();
    setQueue((old) => {
      const ids = new Set(incoming.map((n) => n.id));
      // A poll is the server's word: queued ones it no longer returns were read
      // elsewhere or expired (the one on screen stays until closed).
      const kept =
        pollStartedAt === null
          ? old
          : old.filter((n, i) => i === 0 || ids.has(n.id) || (receivedAt.current.get(n.id) ?? 0) >= pollStartedAt);
      const have = new Set(kept.map((n) => n.id));
      const fresh = incoming.filter((n) => !have.has(n.id) && !acked.current.has(n.id));
      if (fresh.length === 0 && kept.length === old.length) return old;
      for (const n of fresh) receivedAt.current.set(n.id, now);
      const [head, ...rest] = kept;
      const sorted = [...rest, ...fresh].sort((a, b) => a.id - b.id);
      return head ? [head, ...sorted] : sorted;
    });
  }, []);

  useEffect(() => {
    setQueue([]);
    receivedAt.current.clear();
    acked.current.clear();
    if (!userId) return;
    let live = true;
    const poll = async () => {
      const startedAt = Date.now();
      const res = await api.notices();
      if (live && res.ok) add(res.value.notices, startedAt);
    };
    const tick = () => {
      if (document.visibilityState === "visible") void poll();
    };
    const onVisibility = () => tick();
    void poll();
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    const off = onNotice((n) => add([n], null));
    return () => {
      live = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
      off();
    };
  }, [userId, add]);

  const current = queue[0];
  const close = useCallback(() => {
    if (!current) return;
    acked.current.add(current.id);
    setQueue((old) => old.filter((n) => n.id !== current.id));
    void api.ackNotice(current.id).then((res) => {
      // Not saved: forget the local ack so the next poll shows it again.
      if (!res.ok && res.error.status !== 401) acked.current.delete(current.id);
    });
  }, [current]);

  if (!current) return null;
  return (
    <Modal
      key={current.id}
      title={current.title || DEFAULT_TITLE}
      onClose={close}
      closeOnBackdrop={false}
      className="team-notice"
      backdropClassName="team-notice-backdrop"
      testId="team-notice"
    >
      <p className="team-notice-text" data-testid="team-notice-text">
        {current.message}
      </p>
      <div className="team-notice-foot">
        <span className="muted small">
          {current.title ? "From the Wizard Poker team" : ""}
          {queue.length > 1 ? `${current.title ? " · " : ""}${queue.length - 1} more` : ""}
        </span>
        <button type="button" className="btn primary" onClick={close} data-testid="team-notice-ok">
          OK
        </button>
      </div>
    </Modal>
  );
}
